import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Plus } from 'lucide-react'
import { geldText } from '@symphenia/shared-types'
import { WAHL_EINTRAG, WahlFenster, useWahlTasten, type PositionsNavigation } from '@/components/positionswahl/PositionsWahl'
import { apiErrorText } from '@/lib/api-error'
import { fetchRaster } from '../api/calculation-positions.api'
import type { CalcGroup, CalcPosition, CalcRaster } from '../types/calculation.types'
import { NICHT_ANGEBOTEN, ZUSTAND_ANZEIGE } from './PositionsZeile'
import { DateiMarke } from './KalkulationsDateien'
import { PositionAnlegenDialog } from './PositionAnlegenDialog'

const mengeText = (n: number) => n.toLocaleString('de-DE', { maximumFractionDigits: 3 })

/** Die Kennzeile („12 lfm Geländer gerade"): Menge und Einheit in normaler Schrift, die Bezeichnung trägt die Zeile. */
export function Kennzeile({ p }: { p: CalcPosition }) {
  const einheit = p.unitCode ?? p.unitRaw ?? ''
  return (
    <>
      {p.kind !== 'TEXT' && (
        <span className="font-normal">
          {mengeText(p.quantity)}
          {einheit ? ` ${einheit}` : ''}{' '}
        </span>
      )}
      {p.shortText}
    </>
  )
}

/** Gruppen in Dateireihenfolge, Eltern vor Kindern. */
export function geordnet(gruppen: readonly CalcGroup[]): CalcGroup[] {
  const kinder = new Map<string | null, CalcGroup[]>()
  for (const g of gruppen) kinder.set(g.parentId, [...(kinder.get(g.parentId) ?? []), g])
  for (const liste of kinder.values()) liste.sort((a, b) => a.sortOrder - b.sortOrder)
  const aus: CalcGroup[] = []
  const lauf = (parent: string | null) => {
    for (const g of kinder.get(parent) ?? []) {
      aus.push(g)
      lauf(g.id)
    }
  }
  lauf(null)
  return aus
}

/** Die Positionen in der Reihenfolge der Liste: ohne Gruppe zuerst, dann Gruppe für Gruppe. */
function inReihenfolge(raster: CalcRaster): CalcPosition[] {
  return [...raster.positions.filter((p) => !p.groupId), ...geordnet(raster.groups).flatMap((g) => raster.positions.filter((p) => p.groupId === g.id))]
}

/**
 * AP-37 Nachtrag 8 — **die Positionen einer Kalkulation an der Überschrift der geöffneten Position.** Die Liste
 * links neben der Position (Nachtrag 1) ist entfallen, damit das Blatt die Breite bekommt; geblättert und
 * gewählt wird im Positionskopf (N8-2, N8-3).
 *
 * Diese Komponente lädt die Positionen, öffnet ohne Auswahl die erste (N1-18), hält die Aufklappliste und
 * reicht dem Kopf, was er zum Zeichnen braucht (`PositionsNavigation`): Blättern ‹ › mit Zähler und das
 * Fenster — oben „+ Position" (N6-1) und das Suchfeld, darunter die Positionen nach Gruppen mit Nummer bzw.
 * OZ, Kennzeile, Zustand und EP. Die Filter „Zustand" und „Art" gibt es hier nicht mehr (N8-4): Gefiltert
 * wird in der Positionsübersicht.
 */
export function PositionenBereich({
  kalkulationId,
  aktuell,
  stand,
  onOeffnen,
  onErste,
  onAngelegt,
  children,
}: {
  kalkulationId: string
  aktuell: string | null
  /** Stand der Kalkulation — ändert er sich, lädt die Liste neu (EP, Zustand). */
  stand: string
  onOeffnen: (id: string) => void
  /** N1-18: Ohne geöffnete Position öffnet der Reiter die erste — wie im Projekt. */
  onErste?: (id: string) => void
  /**
   * Nachtrag 6 (N6-1): „+ Position" über der Liste — nur, wo der Aufrufer es anbietet (beschreibbar, mit
   * Recht, ohne LV). Bekommt die neue Position und öffnet sie.
   */
  onAngelegt?: (id: string) => void
  /** `anlegen` öffnet den Dialog „Position anlegen" — nur, wo `onAngelegt` gesetzt ist. */
  children: (navigation: PositionsNavigation, anlegen?: () => void) => ReactNode
}) {
  const [raster, setRaster] = useState<CalcRaster | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [suche, setSuche] = useState('')
  const [offen, setOffenRoh] = useState(false)
  const [anlegen, setAnlegen] = useState(false)
  // Nach dem Anlegen lädt die Liste gleich neu — nicht erst, wenn der Stand der Kalkulation nachgezogen ist.
  const [neuLaden, setNeuLaden] = useState(0)

  useEffect(() => {
    fetchRaster(kalkulationId)
      .then((r) => {
        setRaster(r)
        setFehler(null)
      })
      .catch((e) => setFehler(apiErrorText(e, 'Die Positionen ließen sich nicht laden.')))
  }, [kalkulationId, stand, neuLaden])

  const reihe = useMemo(() => (raster ? inReihenfolge(raster) : []), [raster])

  useEffect(() => {
    if (!raster || aktuell || !onErste) return
    if (reihe[0]) onErste(reihe[0].id)
  }, [raster, reihe, aktuell, onErste])

  const setOffen = useCallback((o: boolean) => {
    setOffenRoh(o)
    if (!o) setSuche('')
  }, [])
  const waehle = (id: string) => {
    setOffen(false)
    if (id !== aktuell) onOeffnen(id)
  }
  // Am Ende weiter zum Anfang (wie im Projekt, AP-35 N5-4) — kein toter Knopf am Rand.
  const stelle = reihe.findIndex((p) => p.id === aktuell)
  const schritt = (s: 1 | -1) => {
    if (reihe.length < 2) return
    const i = stelle < 0 ? (s === 1 ? 0 : reihe.length - 1) : (stelle + s + reihe.length) % reihe.length
    waehle(reihe[i].id)
  }
  useWahlTasten({ aktiv: reihe.length > 0, onSchritt: schritt, onOeffnen: () => setOffen(true) })

  const bloecke = useMemo(() => {
    if (!raster) return []
    const q = suche.trim().toLowerCase()
    const gefiltert = raster.positions.filter((p) => !q || `${p.displayNumber} ${p.shortText} ${p.longText ?? ''}`.toLowerCase().includes(q))
    return [
      { id: null as string | null, name: 'ohne Gruppe', nummer: '', ebene: 0, positionen: gefiltert.filter((p) => !p.groupId) },
      ...geordnet(raster.groups).map((g) => ({ id: g.id as string | null, name: g.name, nummer: g.displayNumber, ebene: g.level, positionen: gefiltert.filter((p) => p.groupId === g.id) })),
    ].filter((b) => b.positionen.length > 0)
  }, [raster, suche])

  const alle = raster?.positions.filter((p) => p.kind !== 'TEXT') ?? []
  const ausgepreist = alle.filter((p) => p.state === 'PRICED' || p.state === 'MANUAL').length

  const fenster = (
    <WahlFenster
      suche={suche}
      onSuche={setSuche}
      onSchliessen={() => setOffen(false)}
      kopf={
        onAngelegt && (
          // N6-1: wie „+ Position" in der Positionsliste des Projekts — über der Liste.
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-md bg-primary px-2 py-1 text-xs text-primary-foreground"
            onClick={() => {
              setOffen(false)
              setAnlegen(true)
            }}
          >
            <Plus className="h-3.5 w-3.5" /> Position
          </button>
        )
      }
      fuss={
        <span className="flex flex-wrap justify-between gap-x-3 gap-y-1">
          <span>
            {alle.length} Positionen · {ausgepreist} ausgepreist · {alle.length - ausgepreist} offen
          </span>
          <span>↑↓ wählen · Enter öffnen · Esc schließen</span>
        </span>
      }
    >
      {fehler && <p className="text-xs text-destructive">{fehler}</p>}
      {!raster && !fehler && <p className="text-xs text-muted-foreground">Lädt …</p>}
      {raster && bloecke.length === 0 && <p className="text-xs text-muted-foreground">Keine Position passt zur Suche.</p>}
      {bloecke.map((b) => (
        <div key={b.id ?? 'ohne'}>
          <p className="mb-1 truncate text-xs font-medium uppercase tracking-wide text-muted-foreground" style={{ paddingLeft: `${b.ebene * 0.5}rem` }} title={`${b.nummer} ${b.name}`}>
            {b.nummer && <span className="font-mono normal-case">{b.nummer} </span>}
            {b.name} · {b.positionen.length}
          </p>
          <ul className="space-y-1">
            {b.positionen.map((p) => {
              const z = p.notOffered ? NICHT_ANGEBOTEN : ZUSTAND_ANZEIGE[p.state]
              const nebensache = p.kind === 'OPTIONAL' || p.kind === 'ALTERNATIVE'
              const gewaehlt = aktuell === p.id
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    data-wahl
                    data-aktuell={gewaehlt || undefined}
                    tabIndex={-1}
                    onClick={() => waehle(p.id)}
                    className={`block w-full rounded-md border px-2 py-1.5 text-left ${gewaehlt ? 'border-primary bg-accent' : `border-transparent hover:bg-accent/50 ${WAHL_EINTRAG}`} ${nebensache ? 'opacity-70' : ''}`}
                  >
                    <p className="truncate text-sm" title={p.shortText}>
                      <span className="mr-1 font-mono text-xs text-muted-foreground">{p.displayNumber}</span>
                      <span className="font-medium">
                        <Kennzeile p={p} />
                      </span>
                    </p>
                    <div className="mt-1 flex items-center gap-2">
                      {p.kind !== 'TEXT' && <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${z.klasse}`}>{z.label}</span>}
                      {p.kind === 'OPTIONAL' && <span className="text-[10px] text-muted-foreground">Eventual</span>}
                      {p.kind === 'ALTERNATIVE' && <span className="text-[10px] text-muted-foreground">Wahl</span>}
                      {p.kind === 'TEXT' && <span className="text-[10px] text-muted-foreground">Text</span>}
                      <DateiMarke gesamt={p.fileCount} anZeilen={p.fileCountLines} />
                      <span className="ml-auto text-[11px] tabular-nums text-muted-foreground">{p.kind === 'TEXT' || p.notOffered ? '' : `EP ${geldText(p.unitPrice)}`}</span>
                    </div>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      ))}
    </WahlFenster>
  )

  return (
    <>
      {children({ stelle, anzahl: reihe.length, schritt, offen, setOffen, fenster }, onAngelegt ? () => setAnlegen(true) : undefined)}
      {anlegen && onAngelegt && (
        <PositionAnlegenDialog
          kalkulationId={kalkulationId}
          gruppen={geordnet(raster?.groups ?? [])}
          vorgabeGruppe={raster?.positions.find((p) => p.id === aktuell)?.groupId ?? null}
          onClose={() => setAnlegen(false)}
          onAngelegt={(neu) => {
            setAnlegen(false)
            setNeuLaden((n) => n + 1)
            onAngelegt(neu.id)
          }}
        />
      )}
    </>
  )
}
